/**
 * AS PERDAS — e o que este número não é.
 *
 * ── A FRASE QUE MOLDA A TELA ──────────────────────────────────────────────
 *
 * «O número de peixes que boiam é inferior ao número de peixes que afundam,
 * portanto acaba sendo um reporte não determinístico; o furo aparecerá ao
 * final do ciclo.»
 *
 * É a regra 1 do projeto dita por quem viu, e a tela precisa dizer isso na
 * cara: o que se conta é o que BOIOU, e é um piso. O número real só aparece no
 * saldo residual do encerramento, e é lá que deve ser lido.
 *
 * Isso não é rodapé: é a diferença entre um app que guarda o que foi observado
 * e um app que finge saber o que está dentro da represa.
 *
 * ── DUAS OPÇÕES, E SÓ DUAS ────────────────────────────────────────────────
 *
 * «Não se pede mais que isso a quem está recolhendo peixe morto.» Morte comum
 * ou morte pós-manejo. O banco conhece causas mais finas e elas continuam
 * servindo para importação e análise, mas perguntar isso com o peixe na mão
 * faria a pergunta virar chute registrado como se fosse observação.
 *
 * ── A ÁGUA É OPCIONAL, E É A MESMA LEITURA DA RONDA ───────────────────────
 *
 * O desenho pede que «junto vão qualidade da água, profundidade, temperatura e
 * eventos climáticos anormais». A leitura de água já existe e já tem tela:
 * registrar aqui usa exatamente a mesma rota, e o dado cai no mesmo lugar.
 * Campo novo para a mesma medida garantiria que um dia as duas discordem.
 *
 * O que NÃO entrou foi a profundidade. Não há coluna para o nível da represa
 * nem protocolo de como medi-lo, e inventar os dois — uma régua imaginária,
 * uma unidade escolhida por mim — produziria uma série de números que ninguém
 * saberia comparar. Fica como pergunta ao gestor, e por ora vai na observação.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  listarRonda, registrarLeitura, registrarPerda,
  type CeuDoDia, type PontoDaRonda, type TipoDePerda,
} from '../acesso/api'
import { inteiro, numeroNaFaixa, soNumero } from '../dominio/unidades'
import { hojeISO, SeletorDeDia } from './SeletorDeDia'

const RASCUNHO = 'cunhatai.perdas'

const CEUS: readonly { valor: CeuDoDia; rotulo: string }[] = [
  { valor: 'sol', rotulo: 'sol' },
  { valor: 'parcial', rotulo: 'parcial' },
  { valor: 'nublado', rotulo: 'nublado' },
  { valor: 'chuva', rotulo: 'chuva' },
]

interface Rascunho {
  cicloId: string
  dia: string
  tipo: TipoDePerda
  quantidade: string
  temperatura: string
  oxigenio: string
  ceu: string
  observacao: string
}

function lerRascunho(): Rascunho | null {
  try {
    const bruto = localStorage.getItem(RASCUNHO)
    return bruto ? (JSON.parse(bruto) as Rascunho) : null
  } catch {
    return null
  }
}

function guardarRascunho(r: Rascunho | null): void {
  try {
    if (r) localStorage.setItem(RASCUNHO, JSON.stringify(r))
    else localStorage.removeItem(RASCUNHO)
  } catch {
    /* armazenamento bloqueado: a ficha vale só enquanto a tela estiver aberta */
  }
}

export function Perdas({ aoGravar }: { aoGravar: () => void }) {
  const salvo = useMemo(lerRascunho, [])

  const [pontos, setPontos] = useState<PontoDaRonda[]>([])
  const [cicloId, setCicloId] = useState(salvo?.cicloId ?? '')
  const [dia, setDia] = useState(salvo?.dia ?? hojeISO())
  const [tipo, setTipo] = useState<TipoDePerda>(salvo?.tipo ?? 'mortalidade')
  const [quantidade, setQuantidade] = useState(salvo?.quantidade ?? '')
  const [temperatura, setTemperatura] = useState(salvo?.temperatura ?? '')
  const [oxigenio, setOxigenio] = useState(salvo?.oxigenio ?? '')
  const [ceu, setCeu] = useState(salvo?.ceu ?? '')
  const [observacao, setObservacao] = useState(salvo?.observacao ?? '')
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    listarRonda(dia)
      .then((r) => {
        setPontos(r)
        setCicloId((atual) => (r.some((x) => x.ciclo_id === atual) ? atual : r[0]?.ciclo_id ?? ''))
        setErro(null)
      })
      .catch((falha: unknown) => {
        setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
      })
      .finally(() => setCarregando(false))
  }, [dia])

  useEffect(() => {
    guardarRascunho(
      quantidade !== ''
        ? { cicloId, dia, tipo, quantidade, temperatura, oxigenio, ceu, observacao }
        : null,
    )
  }, [cicloId, dia, tipo, quantidade, temperatura, oxigenio, ceu, observacao])

  const ponto = pontos.find((p) => p.ciclo_id === cicloId) ?? null
  const nQuantidade = numeroNaFaixa(quantidade, 1, 1_000_000)
  const nTemp = numeroNaFaixa(temperatura, 0.1, 49.9)
  const nOxi = numeroNaFaixa(oxigenio, 0, 29.9)

  if (carregando) return <p className="aviso">Carregando…</p>
  // Sem resposta, o app não sabe quais ciclos existem. Dentro do ramo porque
  // `erro` também guarda falha de gravação.
  if (pontos.length === 0) {
    return erro !== null ? (
      <p className="aviso-erro">Não deu para carregar os ciclos: {erro}</p>
    ) : (
      <p className="aviso">Nenhum ciclo aberto neste dia.</p>
    )
  }

  const pronto = ponto !== null && nQuantidade !== null && Number.isInteger(nQuantidade)

  function gravar() {
    if (!ponto || nQuantidade === null) return
    setErro(null)
    try {
      registrarPerda(
        {
          ciclo_id: ponto.ciclo_id,
          data: dia,
          tipo,
          quantidade: nQuantidade,
          observacao: observacao.trim() === '' ? null : observacao.trim(),
        },
        { rotulo: ponto.tanque },
      )

      // A leitura de água é um registro PRÓPRIO, pela rota de sempre. Ela vale
      // por si: se amanhã alguém apagar esta perda, a medição continua sendo
      // uma medição daquele tanque naquele dia.
      //
      // O CÉU SOZINHO NÃO É LEITURA. `POST /api/ambiente` recusa toda leitura
      // sem temperatura e sem oxigênio, e com razão: um registro que só diz
      // «estava nublado» não é medição, é uma linha que finge que alguém mediu.
      // Enfileirar assim mesmo criava um item condenado a ficar recusado na
      // fila para sempre, sem nada que o tratador pudesse fazer a respeito.
      if (nTemp !== null || nOxi !== null) {
        registrarLeitura(
          {
            tanque_id: ponto.tanque_id,
            data: dia,
            hora: null,
            temperatura_c: nTemp,
            oxigenio_mgl: nOxi,
            ph: null,
            alcalinidade_mgl: null,
            ceu: ceu === '' ? null : (ceu as CeuDoDia),
            observacao: 'medida no registro de perda',
          },
          { rotulo: ponto.tanque },
        )
      }

      setQuantidade('')
      setTemperatura('')
      setOxigenio('')
      setCeu('')
      setObservacao('')
      guardarRascunho(null)
      aoGravar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
    }
  }

  return (
    <section className="bloco">
      <h2>Perdas</h2>

      <label className="campo">
        <span className="campo-rotulo">Em qual represa?</span>
        <select value={cicloId} onChange={(e) => setCicloId(e.target.value)}>
          {pontos.map((p) => (
            <option key={p.ciclo_id} value={p.ciclo_id}>
              {p.tanque} · {p.ciclo}
            </option>
          ))}
        </select>
      </label>

      <SeletorDeDia dia={dia} aoMudar={setDia} />

      <div className="apuracao">
        <button
          type="button"
          className={tipo === 'mortalidade' ? 'unidade-botao ativo' : 'unidade-botao'}
          onClick={() => setTipo('mortalidade')}
        >
          morte comum
        </button>
        <button
          type="button"
          className={tipo === 'perda_manejo' ? 'unidade-botao ativo' : 'unidade-botao'}
          onClick={() => setTipo('perda_manejo')}
        >
          morte pós-manejo
        </button>
      </div>

      <label className="campo">
        <span className="campo-rotulo">Peixes recolhidos</span>
        <input
          value={quantidade}
          onChange={(e) => setQuantidade(soNumero(e.target.value))}
          inputMode="numeric"
          placeholder="12"
        />
      </label>

      {/* A frase que molda a tela, e ela fica ANTES de gravar. */}
      <p className="secundario">
        Conta-se o que boiou, e isso é um piso: peixe que afunda não aparece. O número do
        ciclo é o do fechamento.
      </p>

      {ponto && nQuantidade !== null && ponto.peixes_hoje > 0 && (
        <p className="secundario">
          A represa tinha {inteiro(ponto.peixes_hoje)} peixes estimados neste dia.
        </p>
      )}

      {/* ---- A ÁGUA, opcional e pela rota de sempre --------------------- */}
      <h3>Como estava a água</h3>
      <p className="secundario">
        Opcional. Entra como leitura de ronda, no mesmo lugar das outras.
      </p>

      <div className="pesagem-campos">
        <label className="campo">
          <span className="campo-rotulo">Temperatura (°C)</span>
          <input
            value={temperatura}
            onChange={(e) => setTemperatura(soNumero(e.target.value))}
            inputMode="decimal"
            placeholder="28,5"
          />
        </label>
        <label className="campo">
          <span className="campo-rotulo">Oxigênio (mg/L)</span>
          <input
            value={oxigenio}
            onChange={(e) => setOxigenio(soNumero(e.target.value))}
            inputMode="decimal"
            placeholder="2,4"
          />
        </label>
      </div>

      <div className="campo">
        <span className="campo-rotulo">Céu</span>
        <div className="apuracao compacta">
          {CEUS.map((c) => (
            <button
              key={c.valor}
              type="button"
              className={ceu === c.valor ? 'unidade-botao ativo' : 'unidade-botao'}
              // Clicar no marcado desmarca: quem não olhou o céu não responde.
              onClick={() => setCeu(ceu === c.valor ? '' : c.valor)}
            >
              {c.rotulo}
            </button>
          ))}
        </div>
      </div>

      {ceu !== '' && nTemp === null && nOxi === null && (
        <p className="secundario">
          O céu sozinho não vira leitura de água — ela precisa de temperatura ou de
          oxigênio. Sem um dos dois, anote o tempo na observação abaixo.
        </p>
      )}

      <label className="campo">
        <span className="campo-rotulo">O que aconteceu (opcional)</span>
        <input
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="ventania à noite, frente fria, nível baixo"
        />
        <span className="campo-dica">
          Evento climático fora do comum, nível da represa, o que mais tiver chamado atenção.
        </span>
      </label>

      {erro && <p className="aviso-erro">{erro}</p>}

      <button type="button" className="confirma" disabled={!pronto} onClick={gravar}>
        ✓ registrar perda
      </button>
    </section>
  )
}
